import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { DeploymentFrequencyResult } from '../models/team-metrics';
import { TeamMetricsService } from './team-metrics.service';
import { ReleaseReportReleasesResponse } from '../models/release';

@Injectable({
  providedIn: 'root'
})
/**
 * Service responsible for calculating the Deployment Frequency metric.
 * This service fetches release data and determines the number of deployments
 * for a specific team within a given time period.
 */
export class DeploymentFrequencyCalculatorService {

  /**
   * Constructs the DeploymentFrequencyCalculatorService.
   * @param teamMetricsService Service for shared team metrics functionalities and utilities.
   */
  constructor(
    private readonly teamMetricsService: TeamMetricsService
  ) { }

  /**
   * Calculates the Deployment Frequency metric for a given team and period.
   * Optionally includes comparison data from the previous equivalent period.
   * @param teamName The name of the team for which to calculate the metric.
   * @param startDate The start date of the calculation period in ISO format (UTC).
   * @param endDate The end date of the calculation period in ISO format (UTC).
   * @param includeComparison Flag indicating whether to include comparison data. Defaults to true.
   * @returns An Observable emitting the `DeploymentFrequencyResult`.
   */
  public calculate(
    teamName: string,
    startDate: string,
    endDate: string,
    _org: string,
    _project: string,
    includeComparison: boolean = true
  ): Observable<DeploymentFrequencyResult> {
    if (includeComparison) {
      return this.teamMetricsService.addComparisonToMetric(
        (team, start, end) => this.calculateSinglePeriod(team, start, end),
        teamName,
        startDate,
        endDate,
        true,
        'deploymentFrequency'
      );
    }
    return this.calculateSinglePeriod(teamName, startDate, endDate);
  }

  /**
   * Core logic for calculating Deployment Frequency for a single specified period.
   * This method fetches release data, filters it for the specified team,
   * and then calculates the total number of deployments and average deployments per day.
   * @param teamName The name of the team.
   * @param startDate The start date of the period in ISO format (UTC).
   * @param endDate The end date of the period in ISO format (UTC).
   * @returns An Observable emitting the `DeploymentFrequencyResult` for the single period.
   */
  private calculateSinglePeriod(
    teamName: string,
    startDate: string,
    endDate: string
  ): Observable<DeploymentFrequencyResult> {
    return this.teamMetricsService.ensureOwnershipDataLoaded().pipe(
      switchMap(() => this.teamMetricsService.getSmartReleaseData(startDate, endDate)),
      map((fetchedReleasesInRange: ReleaseReportReleasesResponse[]) => {
        const teamReleases = this.teamMetricsService.filterReleasesByTeam(fetchedReleasesInRange, teamName);
        const totalDeployments = teamReleases.length;
        const formattedPeriod = this.teamMetricsService.formatDateRange(startDate, endDate);

        if (totalDeployments === 0) {
          return {
            value: 0,
            displayValue: '0 releases',
            subtitle: 'Avg: N/A',
            totalDeployments: 0,
            averageDeploymentsPerDay: 0,
            period: formattedPeriod
          };
        }

        const startDt = new Date(startDate);
        const endDt = new Date(endDate);
        const diffTime = Math.abs(endDt.getTime() - startDt.getTime());
        const diffDays = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24))); 
        
        const averageDeploymentsPerDay = totalDeployments / diffDays;

        console.log(`[DF Calc Service] Team: ${teamName}, Period: ${formattedPeriod}`);
        console.log(`[DF Calc Service] Total Deployments: ${totalDeployments}, Avg per day: ${averageDeploymentsPerDay.toFixed(1)}`);

        return {
          value: totalDeployments,
          displayValue: `${totalDeployments} release${totalDeployments === 1 ? '' : 's'}`,
          subtitle: `Avg: ${averageDeploymentsPerDay.toFixed(1)} per day`,
          totalDeployments,
          averageDeploymentsPerDay,
          period: formattedPeriod
        };
      }),
      catchError(error => {
        console.error(`[DF Calc Service] Error calculating for team ${teamName} between ${startDate} and ${endDate}:`, error);
        return of({
          value: 0,
          displayValue: '0 releases',
          subtitle: 'Avg: N/A',
          totalDeployments: 0,
          averageDeploymentsPerDay: 0,
          period: this.teamMetricsService.formatDateRange(startDate, endDate)
        });
      })
    );
  }
}
